import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Components
import CircleDangerIcon from '@icons/CircleDangerIcon';
import LinkBlueWithStatus from '@atoms/LinkBlueWithStatus';
import { BodySmall } from '@styled';

// Functionality
import { getIsExpired, getIsExpiring } from '@general/helpers/propertyStatus.helpers';
import { createPropertyExpiredAt, createPropertyActivatedAt } from '@general/helpers/propertyDates.helpers';
import useReactivateProperty from '@hooks/useReactivateProperty';

const PropertyReactivation = ({ propertyId, expiresAt: expiresAtInitial }) => {
    const [expiresAt, setExpiresAt] = useState(expiresAtInitial);
    const [canReactivate, setCanReactivate] = useState(false);
    const [isSuccessfulReactivate, setIsSuccessfulReactivate] = useState(false);
    const [isErrorReactivate, setIsErrorReactivate] = useState(false);

    useEffect(() => {
        setCanReactivate(getIsExpired(expiresAt) || getIsExpiring(expiresAt));
    }, [expiresAt]);

    const [reactivateProperty, { loading: reactivationLoading }] = useReactivateProperty({
        onCompleted: response => {
            console.log({ response });
            setExpiresAt(response.updateProperty.expiresAt);
            setIsSuccessfulReactivate(true);
        },
        onError: () => setIsErrorReactivate(true),
    });

    const onReactivate = () => {
        reactivateProperty({
            variables: {
                id: propertyId,
                activatedAt: createPropertyActivatedAt(),
                expiresAt: createPropertyExpiredAt(),
            },
        });
    };

    if (!canReactivate) {
        return null;
    }

    return (
        <div
            className={`mt-2 p-2 bg-bright-sun-tint text-black d-flex align-items-center ${
                canReactivate ? 'fade-in-fast' : 'fade fade-leave-active'
            }`}
            role="alert"
        >
            <div style={{ lineHeight: '0' }}>
                <CircleDangerIcon color="#000" size="30" stroke="1" />
            </div>
            <div className="d-flex align-items-center ml-2">
                <BodySmall>
                    Your property will no longer display on or after {format(new Date(expiresAt), 'MMMM d, yyyy')}.
                    Reactivate your property to continue promoting it.
                </BodySmall>

                <LinkBlueWithStatus
                    style={{ paddingLeft: '0.5rem' }}
                    onClick={onReactivate}
                    isSubmitting={reactivationLoading}
                    isSuccessful={isSuccessfulReactivate}
                    isError={isErrorReactivate}
                    submittingText="Reactivating"
                    errorText="Failed to Reactivate"
                    successText="Reactivated"
                >
                    Reactivate now.
                </LinkBlueWithStatus>
            </div>
        </div>
    );
};

PropertyReactivation.propTypes = {
    propertyId: PropTypes.string.isRequired,
    expiresAt: PropTypes.string,
};

export default PropertyReactivation;
